import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TableFilterHandler } from 'src/app/core/table-filter-handler';
import { PredefinedFilter } from 'src/app/models/predefined-filter';
import { Search } from 'src/app/models/search';
import { ConnectedUsersStateService } from './connected-users-state.service';
import { ViewsStateService } from './views-state.service';

@Injectable()
export class FolderSearchStateService {
    private onFolderSearchChange = new BehaviorSubject<Search>(null);
    private onFilterHandlerChange = new BehaviorSubject<TableFilterHandler>(null);
    private onFilterConfigurationLoaded = new BehaviorSubject<boolean>(false);

    constructor(
        public connectedUsersStateService: ConnectedUsersStateService,
        public viewsStateService: ViewsStateService
    ) {}

    get onFolderSearchChange$() {
        return this.onFolderSearchChange.asObservable();
    }

    // Retourne la liste des dossiers affichés.
    public getFolderSearch(): Search {
        return this.onFolderSearchChange.value;
    }

    // Spécifie la liste des dossiers affichés.
    public setFolderSearch(search: Search) {
        this.onFolderSearchChange.next(search);
    }

    get onFilterHandlerChange$() {
        return this.onFilterHandlerChange.asObservable();
    }

    // Retourne le gestionnaire de filtre.
    public getFilterHandler(): TableFilterHandler {
        return this.onFilterHandlerChange.value;
    }

    // Spécifie le gestionnaire de filtre.
    public setFilterHandler(filterHandler: TableFilterHandler) {
        this.onFilterHandlerChange.next(filterHandler);
    }

    /**
     * Récupère le contexte de la liste des dossiers d'une vue.
     * @param viewId Id de la vue pour laquelle on veut le contexte.
     * @returns Le contexte de la liste des dossiers d'une vue.
     */
    public getfolderListContext(viewId: number): PredefinedFilter {
        let localStorageValue = localStorage.getItem(this.getLocalStorageKey(viewId));

        if (localStorageValue) {
            return PredefinedFilter.from(JSON.parse(localStorageValue));
        } else {
            return this.getDefaultFolderListContext(viewId);
        }
    }

    /**
     * Spécifit le contexte de la liste des dossiers d'une vue.
     * @param viewId Id de la vue pour laquelle on  veut specifier le contexte.
     * @param folderListContext Instance du contexte de la liste des dossiers.
     */
    private setFolderListContext(viewId: number, folderListContext: PredefinedFilter): void {
        localStorage.setItem(this.getLocalStorageKey(viewId), JSON.stringify(folderListContext));
    }

    /**
     * Récupère la clé utilisée pour stockée le contexte de la liste des dossiers d'une vue.
     * @param viewId : Id de la vue.
     * @returns Clé pour stockée le contexte.
     */
    private getLocalStorageKey(viewId: number) {
        return `folderListContext-${this.connectedUsersStateService.getUserConnected().userId}-${viewId}`;
    }

    /**
     * Récupère le contexte par défaut de la liste des dossiers.
     * @param viewId : Id de la vue.
     * @returns Le contexte de la liste des dossiers d'une vue.
     */
    private getDefaultFolderListContext(viewId: number): PredefinedFilter {
        let autoFilter = new PredefinedFilter();
        autoFilter.viewId = viewId;
        autoFilter.name = 'auto-saved';
        return autoFilter;
    }

    public get onFilterConfigurationLoaded$(): Observable<boolean> {
        return this.onFilterConfigurationLoaded.asObservable();
    }

    // Spécifie l'état de chargement de la configuration des filtres.
    public setFilterConfigurationLoaded(value: boolean) {
        this.onFilterConfigurationLoaded.next(value);
    }

    /************************** NEW ************************/
    private _onPredefinedFilterChange$ = new BehaviorSubject<PredefinedFilter>(null);

    get onPredefinedFilterChange$() {
        return this._onPredefinedFilterChange$.asObservable();
    }

    // Retourne le filtre prédéfini à appliquer à la liste des dossiers.
    public getPredefinedFilterChange(): PredefinedFilter {
        return this._onPredefinedFilterChange$.value;
    }

    // Spécifie le filtre prédéfini à appliquer à la liste des dossiers.
    public setPredefinedFilterChange(filters: PredefinedFilter) {
        this._onPredefinedFilterChange$.next(filters);

        let autoSavedFilter = new PredefinedFilter();
        autoSavedFilter.viewId = this.viewsStateService.getSelectedView().id;
        autoSavedFilter.name = 'auto-saved';
        autoSavedFilter.searchTerm = filters.searchTerm;
        autoSavedFilter.filteredAttributes = filters.filteredAttributes;

        this.setFolderListContext(this.viewsStateService.getSelectedView().id, autoSavedFilter);
    }

    // Spécifie le filtre à appliquer à la liste des dossiers.
    public setFilterChange(filters: PredefinedFilter) {
        this._onPredefinedFilterChange$.next(filters);
    }
}
